---
title: Automatic Database Diagramming and Creation
coverImage: /blog/assets/images/db-diagram.webp
date: 2023-12-02T16:10:00.000Z
updated: 2023-12-05T19:18:34.000Z
excerpt: How I automatically generate Entity-Relationship Diagrams and SQL schemas from markdown.
hidden: true
tags:
  - Database
---

# Todo

- [x] Install ERD and generate the Auth, Blog, and Comment tables diagram
- [ ] make a plugin to automatically parse ERD code blocks in MD to diagrams
- [ ] Make a parser for ERD that creates or verifies an existing DB schema

# What the heck?

While designing the data tables for the various features of this blog, I was writing a kind of loose list of entities, attributes, and relationships. Since I have images for all my blog articles, I thought about making a diagram in excalidraw or something, but in the spirit of DRY coding, I looked into ways to generate the tables.

## Considered Options

I found a few ideas. As I've mentioned in my [blog stack](/blog/articles/blog-stack) article, there are mdx extensions call remark and rehype plugins that will parse markdown. I found one that parses Mermaid diagram definitions and one that does PlantUML, among others. I also thought about using [Kroki](https://github.com/yuzutech/kroki), but running multiple containers just to make some diagrams seems like a bit much.

## ERD

The solution I ended up on was to use [ERD](https://github.com/BurntSushi/erd). It just takes text input and outputs an image. It doesn't automatically hook into my markdown pipeline, but it's a great solution with an easy cli so it should be easy to automate. I'm even thinking of creating my own remark plugin with ERD as a backend.

# SQL Schema generation

Since the ERD syntax is a pretty good subset of SQL, I figured it wouldn't be that hard to make a parser that can output SQL statements to create the tables depicted in the diagram. Things get a bit more complicated once the DB is already running, as I'll need to have some sort of versioning to be able to generate the migrations correctly. That would be a good way to document version history though.
Anyways, I figured it would be fun even if it's not _super_ useful.

## First run at making a parser

I started off asking Bard.Google to write the parser for me, using these prompts.

```
Can you write an algorithm to parse this ERD language and produce a full SQL table schema? a field named id should always have PRIMARY KEY autoincrement.
eg.
[Users]
*id {label: "int"}
username {label: "string"}
password_hash {label: "varchar"}
ip_fingerprint {label: "varchar"}
email {label: "varchar"}
post_notif {label: "bool"}
comment_notif {label: "bool"}

Should produce:
CREATE TABLE IF NOT EXISTS users (
    id integer PRIMARY KEY autoincrement,
    username varchar(20) NOT NULL UNIQUE,
    password_hash varchar(255) NOT NULL,
    ip_fingerprint varchar(255) NOT NULL,
    email varchar(255) NOT NULL,
    post_notif BOOLEAN NOT NULL DEFAULT FALSE,
    comment_notif BOOLEAN NOT NULL DEFAULT FALSE,
);

...
can you make the python version callable from cli and have it take the .er file as a cli argument please?
...
there aren't always both a dataype and constraint, can we make them optional?
```

We landed on this code which is almost correct.

```python: erd_to_sql.py
#!/usr/bin/env python3

def parse_erd(erd_string):
	"""
	Parses an ERD string and generates SQL table schema with specific defaults.

	Args:
		erd_string: The ERD string in the format specified in the prompt.

	Returns:
		A list of SQL CREATE TABLE statements for each entity and their relationships.
	"""

	tables = {}
	relationships = []

	for line in erd_string.splitlines():
		# print(f"{len(line), {line}}")
		if len(line) <= 0:
			continue
		if line[0] == "[":
			# Entity definition
			entity_name = line[1:-1]
			tables[entity_name] = {}
		elif line[0] == "*":
			# Entity attribute
			attribute_name, label = line[1:].split("{")
			label = label[:-1].strip()
			if "," in label:
				data_type, constraints = label.split(",")
				constraints = constraints.split("; ")
			else:
				data_type = label
				constraints = []
			tables[entity_name][attribute_name] = (data_type.strip(), [c.strip() for c in constraints])
		elif line[0:2] == "+-":
			# Many-to-one relationship
			left_entity, right_entity = line[2:].split(" ")
			relationships.append((left_entity, right_entity, "MANY_TO_ONE"))
		elif line[0:2] == "*-":
			# Many-to-many relationship
			left_entity, right_entity = line[2:].split(" ")
			relationships.append((left_entity, right_entity, "MANY_TO_MANY"))
		elif line[0:2] == "*+":
			# One-to-many relationship (implicit)
			left_entity, right_entity = line[2:].split(" ")
			relationships.append((left_entity, right_entity, "ONE_TO_MANY_IMPLICIT"))
		elif line[0:2] == "1-":
			# One-to-one relationship (implicit)
			left_entity, right_entity = line[2:].split(" ")
			relationships.append((left_entity, right_entity, "ONE_TO_ONE_IMPLICIT"))

	# Generate SQL CREATE TABLE statements
	sql_statements = []
	for entity_name, attributes in tables.items():
		sql_statement = f"CREATE TABLE IF NOT EXISTS {entity_name} (\n"
		sql_statement += f"  id INTEGER PRIMARY KEY AUTOINCREMENT,\n"
		for attribute_name, (data_type, constraints) in attributes.items():
			sql_statement += f"  {attribute_name} {data_type} "
		for constraint in constraints:
			if constraint.upper() == "NOT NULL":
				sql_statement += "NOT NULL "
			elif constraint.upper().startswith("DEFAULT"):
				sql_statement += f"DEFAULT {constraint.split()[1]} "
		sql_statement += ",\n"
		sql_statement = sql_statement[:-2] + ");\n"
		sql_statements.append(sql_statement)

	# Generate SQL ALTER TABLE statements for relationships
	for left_entity, right_entity, relationship in relationships:
		if relationship == "MANY_TO_ONE":
			sql_statement = f"ALTER TABLE {left_entity} ADD COLUMN {right_entity}_id INTEGER REFERENCES {right_entity}(id);\n"
		elif relationship == "MANY_TO_MANY":
			sql_statement = f"CREATE TABLE IF NOT EXISTS {left_entity}_{right_entity} (\n"
			sql_statement += f"  {left_entity}_id INTEGER REFERENCES {left_entity}(id),\n"
			sql_statement += f"  {right_entity}_id INTEGER REFERENCES {right_entity}(id),\n"
			sql_statement += "  PRIMARY KEY ({left_entity}_id, {right_entity}_id)\n"
			sql_statement += ");\n"
			sql_statements.append(sql_statement)

	return sql_statements

import argparse

def main():
	parser = argparse.ArgumentParser(description="Parse an ERD file and generate SQL schema")
	parser.add_argument("erd_file", help="Path to the .er file")
	args = parser.parse_args()

	with open(args.erd_file, "r") as f:
		erd_string = f.read()

	sql_statements = parse_erd(erd_string)

	for statement in sql_statements:
		print(statement)

if __name__ == "__main__":
  main()
```
